// src/components/DetalleZonaIndustrial.js
//
// Modal de detalle para Zonas Industriales (tab 3, sublayer 2).
// Misma estructura visual que DetalleParqueIndustrial: header con pill +
// título, galería, secciones con KV. Reutiliza .pdet-* y .parq-popup-*.
//
// Datos del endpoint getIntervencionParamDetalle(idIntervencion, idZonInfra):
//   - lista_param: { infraestructura, valor_descripcion } por campo
//   - nombre_proyecto
// Imágenes: getZonificacionInfraImagenes(idZonInfra).

import React, { useEffect, useState } from "react";
import parquesIndustriales from "../services/parquesService";
import CloseIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibraryOutlined";
import FullscreenIcon from "@mui/icons-material/FullscreenRounded";
import ImageModal from "./ImageModal";
import "./DetalleParqueIndustrial.css";
import "./popup-styles.css";

const capitalizeWords = (str = "") =>
  String(str)
    .toLowerCase()
    .replace(/(^|\s)\S/g, (t) => t.toUpperCase());

const Gallery = ({ images, loading, altPrefix = "Imagen" }) => {
  const [index, setIndex] = useState(0);
  const [fullscreenSrc, setFullscreenSrc] = useState(null);

  useEffect(() => {
    setIndex(0);
  }, [images]);

  if (loading) {
    return (
      <div className="pdet-gallery pdet-gallery-empty">
        <div className="pdet-gallery-spinner" />
        <span>Cargando imágenes…</span>
      </div>
    );
  }
  if (!images || images.length === 0) {
    return (
      <div className="pdet-gallery pdet-gallery-empty">
        <PhotoLibraryIcon style={{ fontSize: 32, opacity: 0.55 }} />
        <span>Sin imágenes disponibles</span>
      </div>
    );
  }

  const prev = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };
  const next = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };
  const openFullscreen = (e) => {
    e?.stopPropagation?.();
    setFullscreenSrc(images[index]);
  };

  return (
    <>
      <div className="pdet-gallery">
        <img
          className="pdet-gallery-img pdet-gallery-img--clickable"
          src={images[index]}
          alt={`${altPrefix} ${index + 1}`}
          onClick={openFullscreen}
          title="Ver en pantalla completa"
        />
        <button
          type="button"
          className="pdet-gallery-fullscreen"
          onClick={openFullscreen}
          aria-label="Ver en pantalla completa"
          title="Ver en pantalla completa"
        >
          <FullscreenIcon style={{ fontSize: 20 }} />
        </button>
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="pdet-gallery-nav left"
              onClick={prev}
              aria-label="Imagen anterior"
            >
              <ChevronLeftIcon style={{ fontSize: 28 }} />
            </button>
            <button
              type="button"
              className="pdet-gallery-nav right"
              onClick={next}
              aria-label="Siguiente imagen"
            >
              <ChevronRightIcon style={{ fontSize: 28 }} />
            </button>
            <div className="pdet-gallery-counter">
              {index + 1} / {images.length}
            </div>
          </>
        )}
      </div>
      <ImageModal
        open={!!fullscreenSrc}
        onClose={() => setFullscreenSrc(null)}
        imageUrl={fullscreenSrc || ""}
        altText={`${altPrefix} ${index + 1} de ${images.length}`}
      />
    </>
  );
};

const KV = ({ label, value }) => {
  if (!value || value === "—" || value === "-" || String(value).trim() === "")
    return null;
  return (
    <div className="parq-popup-kv">
      <span className="parq-popup-kv-label">{label}</span>
      <span className="parq-popup-kv-value">{value}</span>
    </div>
  );
};

const Section = ({ title, children }) => {
  const hasContent = React.Children.toArray(children).some((c) => c);
  if (!hasContent) return null;
  return (
    <div className="parq-popup-section">
      <h5 className="parq-popup-section-title">{title}</h5>
      <div className="parq-popup-grid">{children}</div>
    </div>
  );
};

const DetalleZonaIndustrial = ({
  idIntervencion,
  idZonificacionInfra,
  nombre,
  onClose,
}) => {
  const [data, setData] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingImages, setLoadingImages] = useState(true);

  useEffect(() => {
    if (!idIntervencion || !idZonificacionInfra) return;
    let cancelled = false;
    setLoading(true);
    setLoadingImages(true);
    setData(null);
    setImages([]);

    Promise.all([
      parquesIndustriales
        .getIntervencionParamDetalle(idIntervencion, idZonificacionInfra)
        .catch(() => null),
      parquesIndustriales
        .getZonificacionInfraImagenes(idZonificacionInfra)
        .catch(() => ({ items: [] })),
    ]).then(([detailResp, imgResp]) => {
      if (cancelled) return;

      const item = detailResp?.item;
      const params = item?.lista_param || [];
      const getByName = (n) => {
        const row = params.find(
          (x) =>
            (x.infraestructura || "").toLowerCase() === String(n).toLowerCase()
        );
        return (row?.valor_descripcion || "").trim();
      };

      const nombreParam = getByName("Nombre");
      const glosa = getByName("Glosa");
      const area = getByName("Area");
      const ambito = getByName("Ambito");
      const estado = getByName("Estado");

      const tituloNombre = capitalizeWords(nombreParam);
      const tituloGlosa = glosa && glosa.toLowerCase() !== nombreParam.toLowerCase()
        ? glosa
        : "";
      const nombreFinal = tituloGlosa
        ? `${tituloNombre} - ${tituloGlosa}`
        : tituloNombre;

      setData({
        nombre: nombreFinal || nombre || "Zona Industrial",
        ambito: capitalizeWords(ambito),
        area: area && area !== "-" ? `${area} ha` : "",
        estado,
        nombre_proyecto: capitalizeWords(
          item?.nombre_proyecto || item?.zonificacion_infra || ""
        ),
      });
      setLoading(false);

      const items = imgResp?.items || [];
      const imgs = items
        .map((x) => (x?.url || "").trim())
        .filter(Boolean)
        .map((u) => (u.startsWith("http") ? u : `${window.location.origin}${u}`));
      setImages(imgs);
      setLoadingImages(false);
    });

    return () => {
      cancelled = true;
    };
  }, [idIntervencion, idZonificacionInfra, nombre]);

  return (
    <div className="pdet-card">
      <button
        type="button"
        className="pdet-close"
        onClick={onClose}
        aria-label="Cerrar"
      >
        <CloseIcon style={{ fontSize: 20 }} />
      </button>

      <div className="pdet-card-head">
        <span className="pdet-pill">Zona Industrial</span>
        <h2 className="pdet-title">
          {data?.nombre || nombre || "Zona Industrial"}
        </h2>
      </div>

      <div className="pdet-card-body">
        <Gallery
          images={images}
          loading={loadingImages}
          altPrefix={`Zona ${data?.nombre || nombre || "industrial"} —`}
        />

        {loading ? (
          <div className="pdet-loading">Cargando información…</div>
        ) : (
          data && (
            <>
              <Section title="General">
                <KV label="Ámbito" value={data.ambito} />
                <KV label="Área" value={data.area} />
                <KV label="Estado" value={data.estado} />
                <KV label="Proyecto" value={data.nombre_proyecto} />
              </Section>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default DetalleZonaIndustrial;
